import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AdminService } from '../../../core/admin/admin';
import { Competition } from '../../../core/competitions/competitions';

const PAGE_SIZE = 20;

/** F-009 Milestone C, Slice 6 — mirrors AdminPeopleList's pagination/search
 *  skeleton, but each row navigates to the existing competition-detail page
 *  (routerLink) rather than opening a modal — there's no admin-only detail
 *  view to build here, per the epic's "reuse, don't rebuild" decision. */
@Component({
  selector: 'app-admin-competitions-list',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './admin-competitions-list.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-competitions-list.css',
})
export class AdminCompetitionsList implements OnInit {
  private readonly admin = inject(AdminService);

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly items = signal<Competition[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.total() / PAGE_SIZE)));

  /** "Recalculate scores" — which row is awaiting confirmation, which is
   *  running, and each row's last outcome (kept per competition so several
   *  can be run one after another without losing the earlier messages). */
  readonly confirmingId = signal<number | null>(null);
  readonly recalculatingId = signal<number | null>(null);
  readonly recalcOutcomes = signal<Record<number, { text: string; isError: boolean }>>({});

  readonly searchForm = new FormGroup({
    q: new FormControl('', { nonNullable: true }),
  });

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async search(): Promise<void> {
    this.page.set(1);
    await this.load();
  }

  async goToPage(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages()) return;
    this.page.set(page);
    await this.load();
  }

  dateOnly(iso: string): string {
    return iso.slice(0, 10);
  }

  async confirmRecalculate(c: Competition): Promise<void> {
    if (this.recalculatingId() !== null) return;
    this.recalculatingId.set(c.id);
    this.confirmingId.set(null);
    try {
      const r = await this.admin.recalculateScores(c.id);
      this.setOutcome(c.id, {
        text: `Rebuilt from ${r.deathsProcessed} death${r.deathsProcessed === 1 ? '' : 's'} — awards ${r.awardsBefore} → ${r.awardsAfter}.`,
        isError: false,
      });
    } catch (err) {
      const serverMessage =
        err instanceof HttpErrorResponse && typeof err.error?.message === 'string'
          ? err.error.message
          : null;
      this.setOutcome(c.id, {
        text: serverMessage ?? 'Could not recalculate — try again in a moment.',
        isError: true,
      });
    } finally {
      this.recalculatingId.set(null);
    }
  }

  private setOutcome(id: number, outcome: { text: string; isError: boolean }): void {
    this.recalcOutcomes.update((all) => ({ ...all, [id]: outcome }));
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const q = this.searchForm.getRawValue().q.trim();
      const result = await this.admin.listCompetitions({
        q: q || undefined,
        page: this.page(),
        limit: PAGE_SIZE,
      });
      this.items.set(result.items);
      this.total.set(result.total);
    } catch {
      this.errorMessage.set('Could not load competitions — try again in a moment.');
    } finally {
      this.loading.set(false);
    }
  }
}
